"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.NodeAPIService = void 0;
const ttag_1 = require("ttag");
const errors_1 = require("../../errors");
const nodes_1 = require("../../interface/nodes");
const apiService_1 = require("../apiService");
const uids_1 = require("../uids");
var APIRevisionState;
(function (APIRevisionState) {
    APIRevisionState[APIRevisionState["Draft"] = 0] = "Draft";
    APIRevisionState[APIRevisionState["Active"] = 1] = "Active";
    APIRevisionState[APIRevisionState["Obsolete"] = 2] = "Obsolete";
})(APIRevisionState || (APIRevisionState = {}));
/**
 * Provides API communication for fetching and manipulating nodes metadata.
 *
 * The service is responsible for transforming local objects to API payloads
 * and vice versa. It should not contain any business logic.
 */
class NodeAPIService {
    logger;
    apiService;
    constructor(logger, apiService) {
        this.logger = logger;
        this.apiService = apiService;
        this.logger = logger;
        this.apiService = apiService;
    }
    async getNode(nodeUid, signal) {
        const nodesGenerator = this.iterateNodes([nodeUid], signal);
        const result = await nodesGenerator.next();
        await nodesGenerator.return("finish");
        return result.value;
    }
    // Improvement requested: split into multiple calls for many nodes.
    async *iterateNodes(nodeUids, signal) {
        const allNodeIds = nodeUids.map(uids_1.splitNodeUid);
        const nodeIdsByVolumeId = new Map();
        for (const { volumeId, nodeId } of allNodeIds) {
            if (!nodeIdsByVolumeId.has(volumeId)) {
                nodeIdsByVolumeId.set(volumeId, []);
            }
            nodeIdsByVolumeId.get(volumeId)?.push(nodeId);
        }
        // If the API returns node that is not recognised, it is returned as
        // an error, but first all nodes that are recognised are yielded.
        // Thus we capture all errors and throw them at the end of iteration.
        const errors = [];
        for (const [volumeId, nodeIds] of nodeIdsByVolumeId.entries()) {
            const response = await this.apiService.post(`drive/v2/volumes/${volumeId}/links`, {
                LinkIDs: nodeIds,
            }, signal);
            for (const link of response.Links) {
                try {
                    yield linkToEncryptedNode(this.logger, volumeId, link);
                }
                catch (error) {
                    this.logger.error(`Failed to transform node ${link.Link.LinkID}`, error);
                    errors.push(error);
                }
            }
        }
        if (errors.length) {
            this.logger.warn(`Failed to load ${errors.length} nodes`);
            throw new errors_1.ProtonDriveError((0, ttag_1.c)('Error').t `Failed to load some nodes`, { cause: errors });
        }
    }
    // Improvement requested: load next page sooner before all IDs are yielded.
    async *iterateChildrenNodeUids(parentNodeUid, signal) {
        const { volumeId, nodeId } = (0, uids_1.splitNodeUid)(parentNodeUid);
        let anchor = "";
        while (true) {
            const response = await this.apiService.get(`drive/v2/volumes/${volumeId}/folders/${nodeId}/children?${anchor ? `AnchorID=${anchor}` : ''}`, signal);
            for (const linkID of response.LinkIDs) {
                yield (0, uids_1.makeNodeUid)(volumeId, linkID);
            }
            if (!response.More || !response.AnchorID) {
                break;
            }
            anchor = response.AnchorID;
        }
    }
    // Improvement requested: load next page sooner before all IDs are yielded.
    async *iterateTrashedNodeUids(volumeId, signal) {
        let page = 0;
        while (true) {
            const response = await this.apiService.get(`drive/volumes/${volumeId}/trash?Page=${page}`, signal);
            // The API returns items per shares which is not straightforward to
            // count if there is another page. We had mistakes in the past, thus
            // we rather end when the page is fully empty.
            // The new API endpoint should not split per shares anymore and adopt
            // the new pagination model with More/Anchor. For now, this is not
            // the most efficient way, but should be with us only for a short time.
            let hasItems = false;
            for (const linksPerShare of response.Trash) {
                for (const linkId of linksPerShare.LinkIDs) {
                    yield (0, uids_1.makeNodeUid)(volumeId, linkId);
                    hasItems = true;
                }
            }
            if (!hasItems) {
                break;
            }
            page++;
        }
    }
    async renameNode(nodeUid, originalNode, newNode, signal) {
        const { volumeId, nodeId } = (0, uids_1.splitNodeUid)(nodeUid);
        await this.apiService.put(`drive/v2/volumes/${volumeId}/links/${nodeId}/rename`, {
            Name: newNode.encryptedName,
            NameSignatureEmail: newNode.nameSignatureEmail,
            Hash: newNode.hash,
            OriginalHash: originalNode.hash || null,
        }, signal);
    }
    async moveNode(nodeUid, oldNode, newNode, signal) {
        const { volumeId, nodeId } = (0, uids_1.splitNodeUid)(nodeUid);
        const { nodeId: newParentNodeId } = (0, uids_1.splitNodeUid)(newNode.parentUid);
        await this.apiService.put(`drive/v2/volumes/${volumeId}/links/${nodeId}/move`, {
            ParentLinkID: newParentNodeId,
            NodePassphrase: newNode.armoredNodePassphrase,
            // @ts-expect-error: API accepts NodePassphraseSignature as optional.
            NodePassphraseSignature: newNode.armoredNodePassphraseSignature,
            // @ts-expect-error: API accepts SignatureEmail as optional.
            SignatureEmail: newNode.signatureEmail,
            Name: newNode.encryptedName,
            // @ts-expect-error: API accepts NameSignatureEmail as optional.
            NameSignatureEmail: newNode.nameSignatureEmail,
            Hash: newNode.hash,
            OriginalHash: oldNode.hash,
            ContentHash: newNode.contentHash || null,
        }, signal);
    }
    // Improvement requested: split into multiple calls for many nodes.
    async *trashNodes(nodeUids, signal) {
        const nodeIds = nodeUids.map(uids_1.splitNodeUid);
        const volumeId = assertAndGetSingleVolumeId((0, ttag_1.c)('Operation').t `Trashing items`, nodeIds);
        const response = await this.apiService.post(`drive/v2/volumes/${volumeId}/trash_multiple`, {
            LinkIDs: nodeIds.map(({ nodeId }) => nodeId),
        }, signal);
        // TODO: remove `as` when backend fixes OpenAPI schema.
        yield* handleResponseErrors(nodeUids, volumeId, response.Responses);
    }
    // Improvement requested: split into multiple calls for many nodes.
    async *restoreNodes(nodeUids, signal) {
        const nodeIds = nodeUids.map(uids_1.splitNodeUid);
        const volumeId = assertAndGetSingleVolumeId((0, ttag_1.c)('Operation').t `Restoring items`, nodeIds);
        const response = await this.apiService.put(`drive/v2/volumes/${volumeId}/trash/restore_multiple`, {
            LinkIDs: nodeIds.map(({ nodeId }) => nodeId),
        }, signal);
        // TODO: remove `as` when backend fixes OpenAPI schema.
        yield* handleResponseErrors(nodeUids, volumeId, response.Responses);
    }
    // Improvement requested: split into multiple calls for many nodes.
    async *deleteNodes(nodeUids, signal) {
        const nodeIds = nodeUids.map(uids_1.splitNodeUid);
        const volumeId = assertAndGetSingleVolumeId((0, ttag_1.c)('Operation').t `Deleting items`, nodeIds);
        const response = await this.apiService.post(`drive/v2/volumes/${volumeId}/trash/delete_multiple`, {
            LinkIDs: nodeIds.map(({ nodeId }) => nodeId),
        }, signal);
        // TODO: remove `as` when backend fixes OpenAPI schema.
        yield* handleResponseErrors(nodeUids, volumeId, response.Responses);
    }
    async createFolder(parentUid, newNode) {
        const { volumeId, nodeId: parentId } = (0, uids_1.splitNodeUid)(parentUid);
        const response = await this.apiService.post(`drive/v2/volumes/${volumeId}/folders`, {
            ParentLinkID: parentId,
            NodeKey: newNode.armoredKey,
            NodeHashKey: newNode.armoredHashKey,
            NodePassphrase: newNode.armoredNodePassphrase,
            NodePassphraseSignature: newNode.armoredNodePassphraseSignature,
            SignatureEmail: newNode.signatureEmail,
            Name: newNode.encryptedName,
            Hash: newNode.hash,
            // @ts-expect-error: XAttr is optional as undefined.
            XAttr: newNode.armoredExtendedAttributes,
        });
        return (0, uids_1.makeNodeUid)(volumeId, response.Folder.ID);
    }
    async getRevision(nodeRevisionUid, signal) {
        const { volumeId, nodeId, revisionId } = (0, uids_1.splitNodeRevisionUid)(nodeRevisionUid);
        const response = await this.apiService.get(`drive/v2/volumes/${volumeId}/files/${nodeId}/revisions/${revisionId}?NoBlockUrls=true`, signal);
        return transformRevisionResponse(volumeId, nodeId, response.Revision);
    }
    async getRevisions(nodeUid, signal) {
        const { volumeId, nodeId } = (0, uids_1.splitNodeUid)(nodeUid);
        const response = await this.apiService.get(`drive/v2/volumes/${volumeId}/files/${nodeId}/revisions`, signal);
        return response.Revisions
            .filter((revision) => revision.State === APIRevisionState.Active || revision.State === APIRevisionState.Obsolete)
            .map((revision) => transformRevisionResponse(volumeId, nodeId, revision));
    }
    async restoreRevision(nodeRevisionUid) {
        const { volumeId, nodeId, revisionId } = (0, uids_1.splitNodeRevisionUid)(nodeRevisionUid);
        await this.apiService.post(`drive/v2/volumes/${volumeId}/files/${nodeId}/revisions/${revisionId}/restore`);
    }
    async deleteRevision(nodeRevisionUid) {
        const { volumeId, nodeId, revisionId } = (0, uids_1.splitNodeRevisionUid)(nodeRevisionUid);
        await this.apiService.delete(`drive/v2/volumes/${volumeId}/files/${nodeId}/revisions/${revisionId}`);
    }
}
exports.NodeAPIService = NodeAPIService;
function assertAndGetSingleVolumeId(operationForErrorMessage, nodeIds) {
    const uniqueVolumeIds = new Set(nodeIds.map(({ volumeId }) => volumeId));
    if (uniqueVolumeIds.size !== 1) {
        throw new errors_1.ValidationError((0, ttag_1.c)('Error').t `${operationForErrorMessage} from multiple sections is not allowed`);
    }
    const volumeId = nodeIds[0].volumeId;
    return volumeId;
}
function* handleResponseErrors(nodeUids, volumeId, responses = []) {
    const errors = new Map();
    responses.forEach((response) => {
        if (!response.Response.Code || !(0, apiService_1.isCodeOk)(response.Response.Code) || response.Response.Error) {
            const nodeUid = (0, uids_1.makeNodeUid)(volumeId, response.LinkID);
            errors.set(nodeUid, response.Response.Error || (0, ttag_1.c)('Error').t `Unknown error ${response.Response.Code}`);
        }
    });
    for (const uid of nodeUids) {
        const error = errors.get(uid);
        if (error) {
            yield { uid, ok: false, error };
        }
        else {
            yield { uid, ok: true };
        }
    }
}
function linkToEncryptedNode(logger, volumeId, link) {
    const baseNodeMetadata = {
        // Internal metadata
        hash: link.Link.NameHash || undefined,
        encryptedName: link.Link.Name,
        // Basic node metadata
        uid: (0, uids_1.makeNodeUid)(volumeId, link.Link.LinkID),
        parentUid: link.Link.ParentLinkID ? (0, uids_1.makeNodeUid)(volumeId, link.Link.ParentLinkID) : undefined,
        type: (0, apiService_1.nodeTypeNumberToNodeType)(logger, link.Link.Type),
        creationTime: new Date(link.Link.CreateTime * 1000),
        trashTime: link.Link.TrashTime ? new Date(link.Link.TrashTime * 1000) : undefined,
        // Sharing node metadata
        shareId: link.Sharing?.ShareID || undefined,
        isShared: !!link.Sharing,
        directMemberRole: (0, apiService_1.permissionsToDirectMemberRole)(logger, link.Membership?.Permissions),
    };
    const baseCryptoNodeMetadata = {
        signatureEmail: link.Link.SignatureEmail || undefined,
        nameSignatureEmail: link.Link.NameSignatureEmail || undefined,
        armoredKey: link.Link.NodeKey,
        armoredNodePassphrase: link.Link.NodePassphrase,
        armoredNodePassphraseSignature: link.Link.NodePassphraseSignature,
    };
    if (link.Link.Type === 1 && link.Folder) {
        return {
            ...baseNodeMetadata,
            encryptedCrypto: {
                ...baseCryptoNodeMetadata,
                folder: {
                    armoredExtendedAttributes: link.Folder.XAttr || undefined,
                    armoredHashKey: link.Folder.NodeHashKey,
                },
            },
        };
    }
    if (link.Link.Type === 2 && link.File && link.File.ActiveRevision) {
        return {
            ...baseNodeMetadata,
            totalStorageSize: link.File.TotalEncryptedSize,
            mediaType: link.File.MediaType || undefined,
            encryptedCrypto: {
                ...baseCryptoNodeMetadata,
                file: {
                    base64ContentKeyPacket: link.File.ContentKeyPacket,
                    armoredContentKeyPacketSignature: link.File.ContentKeyPacketSignature || undefined,
                },
                activeRevision: {
                    uid: (0, uids_1.makeNodeRevisionUid)(volumeId, link.Link.LinkID, link.File.ActiveRevision.RevisionID),
                    state: nodes_1.RevisionState.Active,
                    creationTime: new Date(link.File.ActiveRevision.CreateTime * 1000),
                    storageSize: link.File.ActiveRevision.EncryptedSize,
                    signatureEmail: link.File.ActiveRevision.SignatureEmail || undefined,
                    armoredExtendedAttributes: link.File.ActiveRevision.XAttr || undefined,
                    thumbnails: link.File.ActiveRevision.Thumbnails?.map((thumbnail) => transformThumbnail(volumeId, link.Link.LinkID, thumbnail)) || [],
                },
            },
        };
    }
    throw new Error(`Unknown node type: ${link.Link.Type}`);
}
function transformRevisionResponse(volumeId, nodeId, revision) {
    return {
        uid: (0, uids_1.makeNodeRevisionUid)(volumeId, nodeId, revision.ID),
        state: revision.State === APIRevisionState.Active ? nodes_1.RevisionState.Active : nodes_1.RevisionState.Superseded,
        creationTime: new Date(revision.CreateTime * 1000),
        storageSize: revision.Size,
        signatureEmail: revision.SignatureEmail || undefined,
        armoredExtendedAttributes: revision.XAttr || undefined,
        thumbnails: revision.Thumbnails?.map((thumbnail) => transformThumbnail(volumeId, nodeId, thumbnail)) || [],
    };
}
function transformThumbnail(volumeId, nodeId, thumbnail) {
    return {
        // TODO: Legacy thumbnails didn't have ID but we don't have them anymore. Remove typing once API doc is updated.
        uid: (0, uids_1.makeNodeThumbnailUid)(volumeId, nodeId, thumbnail.ThumbnailID),
        // TODO: We don't support any other thumbnail type yet.
        type: thumbnail.Type,
    };
}
//# sourceMappingURL=apiService.js.map